<template>
  <DataGetter
    :url="apiRoute"
    :params="{
      countryISO: countryISO,
      allYears: true,
      startYear,
      indicator,
      ...extraParams,
    }"
    v-slot="{ data }"
    @fetched="setMax"
    class="m-block m-block--charts"
    lazy
  >
    <ChartLayout
      :title="title"
      :units="units && hideUnits ? units : null"
      palette="tints"
    >
      <DataSlicer
        v-if="max"
        :data="data"
        :x-axis-dimension="xAxisDimension"
        :y-axis-dimension="groupDimension"
        value-dimension="value"
        class="sparkline-container"
        :class="{ 'sparkline-container--long-lines': longLines }"
        no-single-value
        v-slot="{ slicedData }"
      >
        <template
          v-for="(series, colorIndex) in [...slicedData].sort((a, b) =>
            a.name < b.name ? -1 : 1
          )"
        >
          <Sparkline
            v-if="
              !exclude.includes(series.name) &&
              !series.data.every((point) => point[1] === null)
            "
            :key="series.name"
            :title="mapNames[series.name] || series.name"
            :units="units"
            show-x-axis
            :marker-style="categories ? 'all' : 'first-last'"
            :hide-units="hideUnits"
            :options="{
              chart: {
                type: chartType,
                marginLeft: 6,
                marginBottom: 24,
                height: 100,
              },
              plotOptions: {
                series: {
                  pointStart: categories ? undefined : startYear,
                },
                area: provisionalYear ? {
                  zoneAxis: 'x',
                  zones: [
                    {
                      value: provisionalYear - 1,
                      className: ''
                    },
                    {
                      className: 'highcharts-translucent'
                    }
                  ]
                } : {}
              },
              yAxis: {
                min: 0,
                max,
                tickAmount: 3,
                plotLines: [
                  { value: 0, className: 'highcharts-zero-axis', zIndex: 3 },
                  /*{ value: max, className: 'highcharts-dotted' },*/
                ],
              },
              xAxis: {
                type: categories ? 'category' : undefined,
                min: categories ? undefined : startYear,
                categories,
                plotLines: provisionalYear ? [
                  { value: provisionalYear - 1, className: 'highcharts-dotted', zIndex: 2 }
                ] : []
              },
              tooltip: {
                valueDecimals: decimals,
                formatter: undefined,
                headerFormat: `<span class='highcharts-header'>${
                  categories ? '{series.name}' : '{point.x}'
                }</span><br>`,
                pointFormatter: function () {
                  return `${
                    categories ? categories[this.x] : series.name
                  }: <b>${parseFloat(this.y.toFixed(decimals))
                    .toLocaleString('fr')
                    .replace(',', '&nbsp;')}${units.replace(
                    / /g,
                    '&nbsp;'
                  )}</b>`;
                },
              },
            }"
            :series="[
              {
                ...series,
                colorIndex:
                  (seriesOrder
                    ? seriesOrder.indexOf(series.name)
                    : colorIndex) % 20,
                data: series.data
                  .filter((point) => categories || point[0] >= startYear)
                  .sort((a, b) => {
                    if (!categories) return 0;
                    if (categories.indexOf(a[0]) > -1)
                      return (
                        categories.indexOf(a[0]) - categories.indexOf(b[0])
                      );
                    if (
                      extraParams[xAxisDimension] &&
                      extraParams[xAxisDimension].indexOf(a[0]) > -1
                    ) {
                      return (
                        extraParams[xAxisDimension].indexOf(a[0]) -
                        extraParams[xAxisDimension].indexOf(b[0])
                      );
                    }
                    return a[0] < b[0] ? -1 : 1;
                  })
                  .map((point) => point[1]),
              },
            ]"
          />
        </template>
      </DataSlicer>
      <template #notes v-if="note">
        {{ note }}
      </template>
    </ChartLayout>
  </DataGetter>
</template>

<script>
import { DataGetter, DataSlicer } from "@international-energy-agency/vue-ui";
import {
  Sparkline,
  ChartLayout,
} from "@international-energy-agency/vue-ui/highcharts";
import techSectorOrder from "../data/techSectorOrder";
export default {
  props: {
    apiRoute: {
      type: String,
      default: "https://api.iea.org/h2020/",
    },
    title: {
      type: String,
      required: false,
    },
    countryISO: {
      type: String,
      required: true,
    },
    indicator: {
      type: String,
      required: true,
    },
    extraParams: {
      type: Object,
      required: false,
    },
    xAxisDimension: {
      type: String,
      default: "year",
    },
    categories: {
      type: Array,
      required: false,
    },
    groupDimension: {
      type: String,
      required: true,
    },
    exclude: {
      type: Array,
      default: () => [],
    },
    units: {
      type: String,
      default: "",
    },
    hideUnits: {
      type: Boolean,
      default: false,
    },
    longLines: {
      type: Boolean,
      default: false,
    },
    decimals: {
      type: Number,
      default: 0,
    },
    mapNames: {
      type: Object,
      default: () => ({}),
    },
    startYear: {
      type: Number,
      default: 2000,
    },
    provisionalYear: {
      type: Number,
      default: null
    },
    note: {
      type: String,
      default: null,
    },
    seriesOrder: {
      type: Array,
      default: () => techSectorOrder,
    },
    chartType: {
      type: String,
      default: "spline",
    },
  },
  components: {
    DataGetter,
    DataSlicer,
    Sparkline,
    ChartLayout,
  },
  data() {
    return {
      max: 0,
    };
  },
  methods: {
    setMax(data) {
      const { exclude, groupDimension, startYear } = this;
      this.max = Math.ceil(
        data
          .filter(
            (row) =>
              row.year >= startYear && !exclude.includes(row[groupDimension])
          )
          .reduce((max, row) => (row.value > max ? row.value : max), 0)
      );
    },
  },
};
</script>

<style>
.sparkline-container {
  display: flex;
  flex-wrap: wrap;
  padding-bottom: 60px;
  justify-content: flex-start;
  gap: 12px;
}
.sparkline {
  flex: 1 1 auto;
  width: 280px;
  max-width: 280px !important;
  border-radius: 8px;
  padding: 8px 16px;
  margin: 0px;
  border: 1px solid #ececec;
}
.sparkline .highcharts-background {
  fill: transparent;
}
.sparkline .highcharts-yaxis-grid .highcharts-grid-line {
  stroke: #e6e6e6;
}
.sparkline-container--long-lines .sparkline__title {
  min-height: 44px !important;
}
.sparkline__title {
  position: relative;
  line-height: 0.875rem;
}
.sparkline__title .m-chart__units {
  position: absolute;
  margin-left: 0px !important;
  padding: 0px;
  bottom: 0px;
}
.sparkline path.highcharts-translucent:not(.highcharts-point) {
  opacity: 0.15;
}
</style>
